import type { ToolUseItem } from '../../../../../contracts/harness.types';
import {
  isRecord,
  readString,
} from '../../../../../contracts/projects/harness/toolResult';
import { toolResultText } from '../../../harness/artifactFromTool';

// Re-export the canonical field readers so adapters import from one local place.
export {
  isRecord,
  readBoolean,
  readNumber,
  readRecordArray,
  readString,
  readToolResultJsonCandidates,
} from '../../../../../contracts/projects/harness/toolResult';
export { toolResultText } from '../../../harness/artifactFromTool';

/**
 * Structured result of a DIRECT Projects harness tool. The BE puts it on the
 * result block's `provider_metadata.data`, which the transcript store maps
 * onto `ToolUseItem.result_provider_metadata.data`. Read it directly —
 * NOT via `readToolResultData`, which also dives into `additional_metadata`
 * (the legacy Evidence-chat persistence path that direct tools never
 * populate). `ToolUseItem.provider_metadata` is the tool-*call* metadata, so
 * we deliberately don't read it here.
 */
export const readToolData = (item: ToolUseItem): Record<string, unknown> | undefined => {
  const data = item.result_provider_metadata?.data;
  return isRecord(data) ? data : undefined;
};

/**
 * Some legacy wrappers nest the useful payload one level deeper
 * (`provider_metadata.data.data`): `get_sessions_by_date`,
 * `search_patient_profiles`, `search_my_templates`,
 * `get_template_and_preferences`.
 */
export const readNestedToolData = (
  item: ToolUseItem
): Record<string, unknown> | undefined => {
  const data = readToolData(item);
  const nested = data?.data;
  return isRecord(nested) ? nested : data;
};

/**
 * Raw tool error text — `provider_metadata.error` (BE's machine string),
 * falling back to the result content display string. Callers wrap with
 * `friendlyToolError(...)` at the render boundary (no double-formatting).
 */
export const readToolError = (item: ToolUseItem): string | undefined =>
  readString(item.result_provider_metadata, 'error') ?? toolResultText(item);

/** Standard `{ download_url, title, filename }` shape for export/generate tools. */
export const readStandardExportPayload = (
  item: ToolUseItem
): {
  url?: string;
  title?: string;
  filename?: string;
} => {
  const data = readToolData(item);
  return {
    url: readString(data, 'download_url'),
    title:
      readString(data, 'title') ??
      readString(item.input, 'title') ??
      readString(data, 'filename'),
    filename: readString(data, 'filename'),
  };
};
